import { useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { Send } from 'lucide-react'
import { toast } from 'sonner'
import { qk } from '@/api/query-keys'
import { getErrorMessage } from '@/api/errors'
import { Button } from '@/components/ui/button'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { paths } from '@/routes/paths'
import { pipelineApi, usePipelineSettings, type ReportDeal } from './api'
import { DAYS, Dot, money, PipelineFrame, shortDay, Stat } from './shared'

/** T2 — the Monday report as it will be posted, built from the board and Friday's check-ins. */
export function ReportPage() {
  const report = useQuery({ queryKey: qk.pipelineReport(), queryFn: pipelineApi.report, staleTime: 60_000 })
  const settings = usePipelineSettings()
  const [sending, setSending] = useState(false)
  const s = settings.data
  const test = async () => {
    setSending(true)
    try {
      const r = await pipelineApi.test('report')
      toast.success(r.result === 'sent' ? 'Sent to you in Slack' : `Not sent: ${r.result}`)
    } catch (e) {
      toast.error(getErrorMessage(e))
    } finally {
      setSending(false)
    }
  }
  return (
    <PipelineFrame
      title="Monday report"
      description={s ? `${s.slack && s.reportChannelId ? `Posted every ${DAYS[s.reportDay]} at ${s.reportHour}:00 Manila time.` : 'Not posted anywhere yet: Slack is off or no channel is set. This is the preview.'} Nobody writes it; it comes from the board.` : 'The week in the pipeline, from the board.'}
      actions={<Button variant="outline" size="sm" onClick={() => void test()} disabled={sending}><Send className="size-4" />Send it to me in Slack</Button>}
    >
      {report.error ? (
        <ErrorState error={report.error} onRetry={() => void report.refetch()} />
      ) : report.isPending ? (
        <LoadingState label="Writing the report" />
      ) : (
        (() => {
          const r = report.data
          return (
            <div className="mx-auto grid max-w-4xl gap-5">
              <p className="text-sm text-muted-foreground">Week of {shortDay(r.weekOf)} · {r.hygiene.line}</p>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {r.forecast ? <Stat label="Open value" value={money(r.forecast.open)} /> : null}
                {r.forecast ? <Stat label="Weighted" value={money(r.forecast.weighted)} /> : null}
                <Stat label="Won this week" value={r.won.length} tone={r.won.length ? 'text-success' : undefined} />
                <Stat label="Moved this week" value={r.movedCount} />
              </div>
              <Block title="Decisions overdue" tone="danger" items={r.decisionsOverdue} extra={(d) => `decision was ${shortDay(d.decisionAt)}`} empty="None. Every pilot decision is on time." />
              <Block title="Decisions this week" items={r.decisionsDue} extra={(d) => shortDay(d.decisionAt)} empty="No decisions due this week." />
              <Block title="Pilots ending in the next two weeks" items={r.pilotsEnding} extra={(d) => `ends ${shortDay(d.endAt)}`} empty="No pilot ends in the next two weeks." />
              <Block title="New pilots" items={r.newPilots} empty="No new pilots this week." />
              <Block title="Won — handed to implementation" items={r.won} empty="Nothing won this week." />
              <Block title="Lost" items={r.lost} extra={(d) => d.reason ?? 'no reason given'} empty="Nothing lost this week." />
              <Block title="Needs action" tone="danger" items={r.needsAttention} extra={(d) => d.reasons.join(' · ')} empty="Nothing red." />
            </div>
          )
        })()
      )}
    </PipelineFrame>
  )
}

function Block<T extends ReportDeal>({ title, items, empty, extra, tone }: { title: string; items: T[]; empty: string; extra?: (d: T) => ReactNode; tone?: 'danger' }) {
  const { workspaceSlug = '' } = useParams()
  return (
    <section className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <h2 className={`mb-2 text-sm font-semibold ${tone === 'danger' && items.length ? 'text-danger' : ''}`}>{title} <span className="num font-normal text-muted-foreground">{items.length}</span></h2>
      {items.length ? (
        <ul className="grid gap-1.5 text-sm">
          {items.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center gap-x-2">
              <Dot level={d.level} />
              <Link to={`${paths.pipeline(workspaceSlug)}?deal=${encodeURIComponent(d.id)}`} className="font-medium hover:text-brand">{d.company}</Link>
              <span className="text-muted-foreground">{d.stageLabel} · {d.owner ?? 'No owner'}{d.value ? ` · ${money(d.value)}` : ''}</span>
              {extra ? <span className="text-xs text-muted-foreground">— {extra(d)}</span> : null}
              {d.nextStep ? <span className="w-full pl-4 text-xs text-muted-foreground">Next: {d.nextStep}</span> : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">{empty}</p>
      )}
    </section>
  )
}
